import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import EmailTemplateEditor from "@/components/EmailTemplateEditor";

export default function EmailSettingsPanel({ s, setS, onSave }) {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [triggering, setTriggering] = useState(false);

  const loadLogs = async () => {
    try {
      const { data } = await api.get("/emails/logs");
      setLogs(data || []);
    } catch (_) { setLogs([]); }
  };
  useEffect(() => { loadLogs(); }, []);

  const runHuNow = async () => {
    setTriggering(true);
    try {
      await api.post("/emails/run-hu-reminders-now");
      toast.success(t("common.save"));
      loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setTriggering(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="surface rounded-xl p-6 space-y-4" data-testid="email-settings-block">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
          <Mail className="h-3.5 w-3.5" /> {t("settings.email_settings")}
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">{t("settings.email_from_name")}</Label>
            <Input
              value={s.email_from_name || ""}
              onChange={(e) => setS({ ...s, email_from_name: e.target.value })}
              className="bg-[#171B24] border-[#2E3241]"
              placeholder="z. B. AutoRefit"
              data-testid="email-from-name"
            />
            <div className="text-[11px] text-zinc-500 mt-1">
              {t("settings.email_from_name_hint")}
            </div>
          </div>
          <div>
            <Label className="text-xs">{t("settings.email_reply_to")}</Label>
            <Input
              type="email"
              value={s.email_reply_to || ""}
              onChange={(e) => setS({ ...s, email_reply_to: e.target.value })}
              className="bg-[#171B24] border-[#2E3241]"
              placeholder="info@meinewerkstatt.de"
              data-testid="email-reply-to"
            />
            <div className="text-[11px] text-zinc-500 mt-1">
              {t("settings.email_reply_to_hint")}
            </div>
          </div>
        </div>

        <div>
          <Label className="text-xs">{t("settings.email_signature")}</Label>
          <Textarea
            value={s.email_signature || ""}
            onChange={(e) => setS({ ...s, email_signature: e.target.value })}
            rows={5}
            className="bg-[#171B24] border-[#2E3241]"
            data-testid="email-signature"
          />
          <div className="text-[11px] text-zinc-500 mt-1">
            {t("settings.email_signature_hint")}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 items-end">
          <div>
            <Label className="text-xs">{t("settings.hu_reminder_days")}</Label>
            <Input
              type="number"
              min="1"
              max="180"
              value={s.hu_reminder_days ?? 30}
              onChange={(e) => setS({ ...s, hu_reminder_days: parseInt(e.target.value || "0", 10) })}
              className="bg-[#171B24] border-[#2E3241] max-w-[160px]"
              data-testid="hu-reminder-days"
            />
            <div className="text-[11px] text-zinc-500 mt-1">
              {t("settings.hu_reminder_days_hint")}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button onClick={onSave} className="bg-blue-500 hover:bg-blue-600" data-testid="email-settings-save">
            {t("common.save")}
          </Button>
          <Button
            onClick={runHuNow}
            disabled={triggering}
            variant="outline"
            className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
            data-testid="run-hu-now-btn"
          >
            <Send className="h-4 w-4 mr-1" /> {t("emails.run_hu_now")}
          </Button>
        </div>
      </div>

      <EmailTemplateEditor />

      <div className="surface rounded-xl p-6">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">
          {t("emails.log_title")}
        </div>
        {logs.length === 0 ? (
          <div className="text-sm text-zinc-500 italic">{t("emails.log_empty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-zinc-400">
                <tr>
                  <th className="text-left py-2 text-xs uppercase tracking-widest">{t("emails.log_when")}</th>
                  <th className="text-left py-2 text-xs uppercase tracking-widest">{t("emails.log_recipient")}</th>
                  <th className="text-left py-2 text-xs uppercase tracking-widest">{t("emails.log_subject")}</th>
                  <th className="text-left py-2 text-xs uppercase tracking-widest">{t("emails.log_by")}</th>
                  <th className="text-left py-2 text-xs uppercase tracking-widest">{t("emails.log_status")}</th>
                </tr>
              </thead>
              <tbody data-testid="email-log-body">
                {logs.map((l) => (
                  <tr key={l.id} className="border-t border-[#2E3241]">
                    <td className="py-2 text-xs font-mono-stat text-zinc-400">
                      {l.sent_at ? new Date(l.sent_at).toLocaleString() : ""}
                    </td>
                    <td className="py-2 text-zinc-200">{l.customer_email}</td>
                    <td className="py-2 text-zinc-300 max-w-[380px] truncate">{l.subject}</td>
                    <td className="py-2 text-zinc-400 text-xs">
                      {l.trigger === "auto_hu" ? t("emails.log_auto") : (l.sent_by_name || t("emails.log_manual"))}
                    </td>
                    <td className="py-2">
                      <span className={`text-xs uppercase tracking-wider ${
                        l.status === "sent" ? "text-emerald-400" : "text-red-400"
                      }`}>
                        {l.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
